/* Technical Outdoor guest registration on the storefront. No credentials are
 * placed in URLs, cookies, analytics, or browser history. */
(() => {
  'use strict';

  const API_ORIGIN = 'https://kachang-hub.onrender.com';
  const API_PATH = '/public/event-registration';
  const STORAGE_KEY = 'to-event-registration-state-v1';
  const DRAFT_KEY = 'to-event-registration-draft-v1';
  const DRAFT_TTL = 30 * 60 * 1000;
  const RETURN_KEY = 'to-event-checkout-return-v1';
  const RETURN_TTL = 24 * 60 * 60 * 1000;
  const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
  const REFERENCE = /^TO-[A-Z0-9-]{4,80}$/;
  const EVENT_PATH = /^\/events\/([a-z0-9]+(?:-[a-z0-9]+)*)(?:\/.*)?$/;
  const ALLOWED_QUERY = new Set(['lang', 'occurrence', 'format', 'download']);
  const REMOVE = 'script,iframe,object,embed,base,meta,link,style,template,svg,math';
  const EDITABLE = /^(?:quantity|registration_option|contact_email|contact_phone|payment_method|participant_(?:[1-9]|1[0-2])(?:_age|_sex|_q_[A-Za-z0-9_-]{1,40})?)$/;
  const BOOKING_STAGES = new Set(['tickets', 'contact', 'participants', 'payment', 'review']);
  const CARRY_EVENT = 'carry-your-journey-deuter-opinel-ddcd50';
  const CARRY_OCCURRENCE = 'ab355dad0ad347ba93206e0d203a2676';
  const CARRY_KEY = 'carry_journey_20261107';

  function track(name, payload) {
    try { return window.TOIAnalytics?.track(name, payload) === true; }
    catch { return false; /* Analytics cannot block booking. */ }
  }
  function analyticsContext() {
    try { return window.TOIAnalytics?.analyticsContext?.() || null; }
    catch { return null; }
  }

  function language(url, fallback = 'id') {
    // Shopify's localized page is the same authority as the website header.
    // Old links can retain the previous query language after a header switch.
    if (url.pathname === '/en/pages/event-registration') return 'en';
    if (url.pathname === '/pages/event-registration') return 'id';
    const requested = url.searchParams.get('lang');
    if (requested === 'id' || requested === 'en') return requested;
    return url.pathname.startsWith('/en/') ? 'en' : fallback;
  }

  function internalPath(input) {
    if (typeof input !== 'string' || !input || /[\u0000-\u001f\\]/.test(input)) return null;
    let url;
    try { url = new URL(input, API_ORIGIN); } catch { return null; }
    if (url.origin !== API_ORIGIN || url.username || url.password || url.hash ||
        !url.pathname.startsWith('/events/')) return null;
    if (!EVENT_PATH.test(url.pathname) || [...url.searchParams.keys()].some((key) => !ALLOWED_QUERY.has(key))) return null;
    if (url.searchParams.get('lang') && !['id', 'en'].includes(url.searchParams.get('lang'))) return null;
    return url.pathname + url.search;
  }

  function pagePath(pageUrl) {
    const url = new URL(pageUrl);
    const lang = language(url);
    const event = url.searchParams.get('event');
    const booking = url.searchParams.get('booking');
    const recover = url.searchParams.get('recover');
    const view = url.searchParams.get('view');
    if ([event, booking, recover, view].filter(Boolean).length !== 1) return null;
    if (event) {
      if (!SLUG.test(event)) return null;
      const target = new URL('/events/' + event, API_ORIGIN);
      const occurrence = url.searchParams.get('occurrence');
      if (occurrence) {
        if (!/^[a-f0-9]{32}$/.test(occurrence)) return null;
        target.searchParams.set('occurrence', occurrence);
      }
      target.searchParams.set('lang', lang);
      return internalPath(target.href);
    }
    if (booking) return REFERENCE.test(booking) ? internalPath('/events/registration/' + booking + '?lang=' + lang) : null;
    // Keep old access links valid; Hub now renders account sign-in here.
    if (recover === '1') return internalPath('/events/registration/recover?lang=' + lang);
    const validatedView = view ? internalPath(view) : null;
    if (!validatedView) return null;
    const target = new URL(validatedView, API_ORIGIN);
    target.searchParams.set('lang', lang);
    return internalPath(target.href);
  }

  function eventIdentity(pageUrl, formOccurrence = null) {
    let url;
    try { url = new URL(pageUrl); } catch { return null; }
    if (!['/pages/event-registration', '/en/pages/event-registration'].includes(url.pathname) ||
        url.searchParams.getAll('event').length !== 1 ||
        url.searchParams.getAll('occurrence').length > 1 || !pagePath(url.href)?.startsWith('/events/')) return null;
    if (formOccurrence !== null && !/^[a-f0-9]{32}$/.test(formOccurrence)) return null;
    return {
      event: url.searchParams.get('event'),
      occurrence: formOccurrence === null ? (url.searchParams.get('occurrence') || '') : formOccurrence,
      lang: language(url),
    };
  }

  function eventKeyForForm(pageUrl, form) {
    const occurrence = form?.elements.namedItem('occurrence_id')?.value;
    const identity = eventIdentity(pageUrl, occurrence);
    if (!identity || identity.event !== CARRY_EVENT || identity.occurrence !== CARRY_OCCURRENCE) return null;
    const url = new URL(pageUrl);
    const action = internalPath(form.getAttribute('action'));
    return url.searchParams.get('occurrence') === CARRY_OCCURRENCE &&
      action && new URL(action, API_ORIGIN).pathname === `/events/${CARRY_EVENT}/register` ? CARRY_KEY : null;
  }
  function bookingPayload(payload, eventKey) {
    return eventKey ? { ...payload, event_key: eventKey } : payload;
  }

  function loginReturnPath(pageUrl, packageHint = false, previewThemeId = '', formOccurrence = null) {
    const identity = eventIdentity(pageUrl, formOccurrence);
    if (!identity) return null;
    const source = new URL(pageUrl);
    const path = new URL((identity.lang === 'en' ? '/en' : '') + '/pages/event-registration', source.origin);
    path.searchParams.set('event', identity.event);
    if (identity.occurrence) path.searchParams.set('occurrence', identity.occurrence);
    path.searchParams.set('lang', identity.lang);
    if (packageHint || source.searchParams.get('package') === 'selektad-2026') {
      path.searchParams.set('package', 'selektad-2026');
    }
    const preview = source.searchParams.get('preview_theme_id') || previewThemeId;
    if (preview && /^\d{1,20}$/.test(preview)) path.searchParams.set('preview_theme_id', preview);
    return path.pathname + path.search;
  }

  function storefrontLoginUrl(route, pageUrl, packageHint = false, previewThemeId = '', formOccurrence = null, emailHint = '') {
    const returnTo = loginReturnPath(pageUrl, packageHint, previewThemeId, formOccurrence);
    if (!returnTo) return null;
    let url;
    try { url = new URL(route, pageUrl); } catch { return null; }
    if (url.origin !== new URL(pageUrl).origin || url.pathname !== '/customer_authentication/login' ||
        url.username || url.password || url.hash) return null;
    url.searchParams.set('return_to', returnTo);
    url.searchParams.delete('login_hint');
    // Only an explicit sign-in click supplies a previously entered email. The
    // ordinary account-first flow collects it once on Shopify, then prefills it here.
    const hint = typeof emailHint === 'string' ? emailHint.trim().toLowerCase() : '';
    if (hint.length <= 254 && /^[^\s@]+@[^\s@]+$/.test(hint)) url.searchParams.set('login_hint', hint);
    return url.pathname + url.search;
  }

  function fieldLabel(input) {
    return input.labels?.[0]?.textContent?.trim() || '';
  }

  function draftFields(form) {
    const fields = [];
    for (const input of form.querySelectorAll('input[name],select[name],textarea[name]')) {
      if (!EDITABLE.test(input.name) || input.disabled || input.type === 'hidden' ||
          input.type === 'checkbox' || (input.type === 'radio' && !input.checked)) continue;
      fields.push({ name: input.name, value: input.value, type: input.type, label: fieldLabel(input) });
    }
    return fields;
  }

  function parseDraft(raw, identity, now = Date.now()) {
    let draft;
    try { draft = JSON.parse(raw); } catch { return null; }
    if (!draft || draft.version !== 1 || !identity || draft.event !== identity.event ||
        draft.occurrence !== identity.occurrence || draft.lang !== identity.lang ||
        !Number.isSafeInteger(draft.created) || draft.created > now || now - draft.created > DRAFT_TTL ||
        !Array.isArray(draft.fields) || draft.fields.length > 200 ||
        draft.fields.some((field) => !field || !EDITABLE.test(field.name) ||
          typeof field.value !== 'string' || field.value.length > 500 ||
          typeof field.type !== 'string' || typeof field.label !== 'string')) return null;
    return draft;
  }

  function restoreDraft(form, draft) {
    let restored = 0;
    const controls = [...form.querySelectorAll('input[name],select[name],textarea[name]')];
    for (const saved of draft.fields) {
      const choices = controls.filter((input) => input.name === saved.name &&
        input.type === saved.type && fieldLabel(input) === saved.label && !input.disabled &&
        input.type !== 'hidden' && input.type !== 'checkbox');
      const input = saved.type === 'radio' ? choices.find((choice) => choice.value === saved.value) : choices[0];
      if (!input) continue;
      if (input.tagName === 'SELECT' && ![...input.options].some((option) => option.value === saved.value && !option.disabled)) continue;
      if (saved.type === 'radio') input.checked = true;
      else input.value = saved.value;
      restored += 1;
    }
    return restored;
  }

  function storeDraft(storage, draft) {
    const serialized = JSON.stringify(draft);
    if (serialized.length > 65536) throw Error('draft too large');
    storage.setItem(DRAFT_KEY, serialized);
  }

  function accountCheckIsCurrent(request, latest, checkedEmail, currentEmail, connected) {
    return request === latest && checkedEmail === currentEmail && connected;
  }

  function registrationAccountState(customerEmail, contactEmail) {
    const account = String(customerEmail || '').trim().toLowerCase();
    const contact = String(contactEmail || '').trim().toLowerCase();
    if (!account) return 'sign_in';
    return account === contact ? 'signed_in' : 'mismatch';
  }

  function prefillAccountFields(form, profile) {
    const email = form.elements.namedItem('contact_email');
    if (!email || !profile.email) return;
    if (!email.value.trim()) email.value = profile.email;
    if (registrationAccountState(profile.email, email.value) !== 'signed_in') return;
    for (const [name, value] of [['contact_phone', profile.phone], ['participant_1', profile.name]]) {
      const input = form.elements.namedItem(name);
      if (input && !input.value.trim() && typeof value === 'string' && value.trim()) {
        input.value = value.trim().slice(0, input.maxLength > 0 ? input.maxLength : 300);
      }
    }
  }

  function siteUrl(path, lang, siteOrigin = location.origin) {
    const valid = internalPath(path);
    if (!valid) return null;
    const source = new URL(valid, API_ORIGIN);
    const website = new URL((lang === 'en' ? '/en' : '') + '/pages/event-registration', siteOrigin);
    website.searchParams.set('lang', lang);
    const parts = source.pathname.split('/');
    if (parts[2] === 'registration' && parts[3] === 'recover') {
      website.searchParams.set('recover', '1');
    } else if (parts[2] === 'registration' && REFERENCE.test(parts[3] || '') && parts.length === 4) {
      website.searchParams.set('booking', parts[3]);
    } else if (parts.length === 3 && SLUG.test(parts[2])) {
      website.searchParams.set('event', parts[2]);
      if (source.searchParams.get('occurrence')) website.searchParams.set('occurrence', source.searchParams.get('occurrence'));
    } else {
      website.searchParams.set('view', valid);
    }
    return website.href;
  }

  function isBinaryPath(path) {
    const url = new URL(path, API_ORIGIN);
    return /^\/events\/poster\//.test(url.pathname) ||
      /^\/events\/registration\/TO-[A-Z0-9-]+\/(?:ticket|file)\//.test(url.pathname);
  }

  function checkoutUrl(value, lang) {
    if (lang !== 'id' && lang !== 'en') return null;
    let url;
    try { url = new URL(value); } catch { return null; }
    if (url.protocol !== 'https:' || url.username || url.password || url.port) return null;
    const shop = url.hostname === 'vfb1m1-x7.myshopify.com' || url.hostname === 'checkout.shopify.com';
    const brand = url.hostname === 'www.technicaloutdoor.com' || url.hostname === 'technicaloutdoor.com';
    if ((shop || brand) && /^\/(?:[^/]+\/)*(?:invoices|checkouts)\/[A-Za-z0-9_-]+(?:\/|$)/.test(url.pathname)) {
      // Reused Shopify invoices can remember a different checkout language.
      url.searchParams.set('locale', lang);
      return url.href;
    }
    return null;
  }

  function bookingContext(pageUrl) {
    const path = pagePath(pageUrl);
    if (!path) return null;
    const url = new URL(path, API_ORIGIN);
    const match = /^\/events\/registration\/(TO-[A-Z0-9-]{4,80})(?:\/checkout)?$/.exec(url.pathname);
    return match ? { booking: match[1], lang: language(url) } : null;
  }

  function autoCheckoutPost(path, fields, booking) {
    const url = new URL(path, API_ORIGIN);
    if (url.pathname === `/events/registration/${booking.booking}/checkout`) return true;
    return /^\/events\/[a-z0-9]+(?:-[a-z0-9]+)*\/register$/.test(url.pathname) &&
      fields.payment_method === 'online';
  }

  function checkoutReturn(raw, now = Date.now()) {
    let saved;
    try { saved = JSON.parse(raw); } catch { return null; }
    if (!saved || typeof saved.booking !== 'string' || !REFERENCE.test(saved.booking) ||
        !['id', 'en'].includes(saved.lang) || !Number.isSafeInteger(saved.created) ||
        saved.created > now || now - saved.created >= RETURN_TTL) return null;
    return (saved.lang === 'en' ? '/en' : '') + '/pages/event-registration?lang=' +
      saved.lang + '&booking=' + encodeURIComponent(saved.booking);
  }

  function resumeCheckout() {
    // Only the tab that started event checkout is redirected. A return is not
    // payment evidence: the registration page reads the verified Hub status.
    if (!['/', '/en', '/en/'].includes(location.pathname)) return;
    try {
      const raw = sessionStorage.getItem(RETURN_KEY);
      if (!raw) return;
      sessionStorage.removeItem(RETURN_KEY);
      const destination = checkoutReturn(raw);
      if (destination) location.replace(destination);
    } catch { /* Ordinary storefront navigation still works without storage. */ }
  }

  function payloadError(status, code) {
    const error = new Error('registration_unavailable');
    error.status = status;
    error.code = code || '';
    return error;
  }

  async function bridge(api, path, method, fields, state) {
    const valid = internalPath(path);
    if (!valid || !['GET', 'POST'].includes(method)) throw payloadError(400, 'invalid_path');
    const accountCheck = method === 'POST' && /^\/events\/[a-z0-9]+(?:-[a-z0-9]+)*\/account-check\?lang=(?:id|en)$/.test(valid);
    const analyticsPost = method === 'POST' && /^\/events\/registration\/TO-[A-Z0-9-]{4,80}\/analytics(?:\?lang=(?:id|en))?$/.test(valid);
    const onlineBooking = method === 'POST' && fields.payment_method === 'online' &&
      /^\/events\/[a-z0-9]+(?:-[a-z0-9]+)*\/register(?:\?lang=(?:id|en))?$/.test(valid);
    const context = onlineBooking || (analyticsPost && fields.action === 'recover') ? analyticsContext() : null;
    const response = await fetch(api, {
      method: 'POST', mode: 'cors', credentials: 'omit', redirect: 'error', cache: 'no-store',
      referrerPolicy: 'no-referrer', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json, image/png, application/pdf, application/octet-stream' },
      body: JSON.stringify({ path: valid, method, fields, state, ...(context ? { analytics_context: context } : {}) }),
    });
    const type = response.headers.get('Content-Type') || '';
    if (!type.toLowerCase().includes('application/json')) {
      if (!response.ok) throw payloadError(response.status);
      return { binary: await response.blob(), type, disposition: response.headers.get('Content-Disposition') || '' };
    }
    let data;
    try { data = await response.json(); } catch { throw payloadError(response.status); }
    if (analyticsPost) {
      if (!response.ok || !['queued', 'existing', 'ineligible', 'review', 'unavailable', 'withdrawn'].includes(data?.status)) {
        throw payloadError(response.status, data?.code);
      }
      return { analyticsStatus: data.status };
    }
    if (accountCheck) {
      if (!response.ok || !data || typeof data.exists !== 'boolean') {
        throw payloadError(data?.status || response.status, data?.code);
      }
      return { exists: data.exists };
    }
    if (!data || typeof data !== 'object' || typeof data.html !== 'string') {
      throw payloadError(data?.status || response.status, data?.code);
    }
    return data;
  }

  function filenameFrom(disposition, fallback) {
    const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
    const plain = /filename="?([^";]+)"?/i.exec(disposition);
    let name = fallback;
    try { if (encoded) name = decodeURIComponent(encoded[1]); else if (plain) name = plain[1]; } catch { /* use fallback */ }
    return name.replace(/[^A-Za-z0-9._-]/g, '_').slice(0, 100) || fallback;
  }

  function updateRegistrationControls(main, quantity, lang) {
    const count = Number(quantity.value);
    const maximum = Math.max(0, ...Array.from(quantity.options, (option) => Number(option.value))
      .filter((value) => Number.isSafeInteger(value) && value >= 1));
    const choice = main.querySelector('[name="registration_option"]:checked');
    const total = main.querySelector('[data-oe-total]');
    if (total && Number.isInteger(count) && count >= 1 && count <= maximum) {
      const unit = Number(choice?.dataset.oeUnit ?? total.dataset.oeTotal);
      if (Number.isSafeInteger(unit) && unit >= 0) {
        const locale = lang === 'id' ? 'id-ID' : 'en-US';
        total.textContent = `Rp ${unit.toLocaleString(locale)} × ${count} = Rp ${(unit * count).toLocaleString(locale)} IDR`;
      }
    }
    let methods = null;
    try { methods = choice?.dataset.oeMethods ? JSON.parse(choice.dataset.oeMethods) : null; } catch { methods = []; }
    main.querySelectorAll('[data-oe-package-consent]').forEach((input) => { input.required = !!methods; });
    main.querySelectorAll('[name="payment_method"]').forEach((input) => {
      input.disabled = !!methods && !methods.includes(input.value);
      if (input.disabled) input.checked = false;
    });
    main.querySelectorAll('[data-oe-person]').forEach((row) => {
      const visible = Number(row.dataset.oePerson) <= count;
      row.hidden = !visible;
      row.querySelectorAll('[data-oe-required]').forEach((input) => { input.required = visible; });
    });
  }

  function selectRequestedPackage(main, pageUrl) {
    // A landing-page hint may select only an option the current Hub form offers.
    // This is called for the first GET only, never over a submitted form's result.
    const url = new URL(pageUrl);
    if (!url.searchParams.get('event') || url.searchParams.getAll('package').length !== 1
      || url.searchParams.get('package') !== 'selektad-2026') return false;
    const choice = main.querySelector('input[type="radio"][name="registration_option"][value="selektad-2026"]');
    if (!choice || choice.matches(':disabled')) return false;
    choice.checked = true;
    return true;
  }

  function install() {
    const root = document.querySelector('[data-to-event-registration]');
    if (!root) {
      resumeCheckout();
      window.addEventListener('pageshow', resumeCheckout);
      return;
    }
    function clearCheckoutReturn() {
      // Back from checkout or a direct ticket return cancels the homepage detour.
      if (!bookingContext(location.href)) return;
      try { sessionStorage.removeItem(RETURN_KEY); } catch { /* handled by readState */ }
    }
    clearCheckoutReturn();
    window.addEventListener('pageshow', clearCheckoutReturn);
    const content = root.querySelector('[data-registration-content]');
    const status = root.querySelector('[data-registration-status]');
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'to-event-registration__retry';
    retry.hidden = true;
    status.after(retry);
    const api = root.dataset.api || API_ORIGIN + API_PATH;
    const initialIdentity = eventIdentity(location.href);
    const initialEventUrl = location.href;
    const initialStart = new URL(initialEventUrl).searchParams.getAll('start');
    let startPending = initialStart.length === 1 && initialStart[0] === '1';
    if (new URL(initialEventUrl).searchParams.has('start')) {
      const clean = new URL(initialEventUrl);
      clean.searchParams.delete('start');
      history.replaceState({}, '', clean.pathname + clean.search + clean.hash);
    }
    const initialPackageHint = new URL(location.href).searchParams.get('package') === 'selektad-2026';
    const initialPreviewThemeId = new URL(location.href).searchParams.get('preview_theme_id') || '';
    const customerEmail = (root.dataset.customerEmail || '').trim().toLowerCase();
    let lang = language(new URL(location.href), root.dataset.language || 'id');
    let state = null;
    let busy = false;
    let blobs = [];
    let renderVersion = 0;
    let failedLoad = null;
    let analyticsQueue = Promise.resolve();
    const analyticsSent = new Set();
    function currentEventKey(form) {
      const direct = eventKeyForForm(location.href, form);
      if (direct) return direct;
      const current = new URL(location.href);
      // Hub canonicalization can omit the occurrence after its initial GET.
      if (current.searchParams.get('event') !== CARRY_EVENT || current.searchParams.has('occurrence')) return null;
      return eventKeyForForm(initialEventUrl, form);
    }

    function syncBookingAnalytics() {
      // This private endpoint changes analytics delivery only. It never creates
      // bookings, retries checkout, or treats a browser return as payment proof.
      analyticsQueue = analyticsQueue.then(async () => {
        const marker = content.querySelector('[data-oe-analytics-action]');
        const path = internalPath(marker?.dataset.oeAnalyticsAction || '');
        const csrf = marker?.dataset.oeAnalyticsCsrf;
        if (!path || !/^\/events\/registration\/TO-[A-Z0-9-]{4,80}\/analytics$/.test(path) || !csrf) return;
        const permission = window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.();
        const action = permission === false ? 'withdraw' : permission === true &&
          marker.dataset.oeAnalyticsRecover === 'true' && analyticsContext() ? 'recover' : null;
        if (!action) return;
        const key = path + ':' + action;
        if (analyticsSent.has(key)) return;
        const result = await bridge(api, path, 'POST', { csrf, action }, state);
        if (result.analyticsStatus === 'unavailable') return;
        analyticsSent.add(key);
        // A later explicit withdrawal must win even if a recovery was in flight.
        if (action === 'recover') analyticsSent.delete(path + ':withdraw');
      }).catch(() => {}); // Tracking must never replace a ticket or block booking.
    }
    document.addEventListener('visitorConsentCollected', syncBookingAnalytics);
    document.addEventListener('toi:analytics-ready', syncBookingAnalytics);

    const labelsEn = {
      load: 'Loading registration…', network: 'We could not confirm the result. Your entries are still here. Check your registration before retrying.',
      failed: 'Registration is unavailable right now. Please try again or contact info@technicaloutdoor.com.',
      storage: 'Private browser storage is required for registration. Enable session storage or use a standard browser window.',
      invalid: 'Open this page from an event in our calendar, or sign in to your account to see your bookings.',
      expired: 'This registration session expired. Sign in to your Technical Outdoor account with the email used to register.',
      copied: 'Copied', copyFailed: 'Select and copy these details manually.', ready: 'Your download is ready.',
      retry: 'Try again',
    };
    const labelsId = {
      load: 'Memuat pendaftaran…', network: 'Kami belum bisa memastikan apakah pendaftaran berhasil. Data yang kamu isi masih ada. Cek pendaftaranmu sebelum mencoba lagi.',
      failed: 'Pendaftaran belum bisa dibuka. Coba lagi atau hubungi info@technicaloutdoor.com.',
      storage: 'Browser perlu menyimpan sesi agar kamu bisa mendaftar. Izinkan penyimpanan sesi, atau coba buka halaman ini di jendela biasa tanpa mode privat.',
      invalid: 'Pilih acara dari kalender kami untuk mendaftar, atau masuk ke akunmu untuk melihat pendaftaran yang sudah ada.',
      expired: 'Sesi pendaftaran sudah berakhir. Masuk ke akun Technical Outdoor dengan email yang kamu pakai saat mendaftar.',
      copied: 'Berhasil disalin', copyFailed: 'Blok teks ini, lalu salin.', ready: 'File siap diunduh.',
      retry: 'Coba lagi',
    };
    let labels = lang === 'en' ? labelsEn : labelsId;
    retry.textContent = labels.retry;
    retry.addEventListener('click', () => {
      if (!failedLoad || busy) return;
      const { path, navigation, freshRetry, packageUrl } = failedLoad;
      void load(path, navigation, freshRetry, packageUrl);
    });

    function setLanguage(next) {
      if (next !== 'en' && next !== 'id') return;
      lang = next;
      labels = lang === 'en' ? labelsEn : labelsId;
      retry.textContent = labels.retry;
      root.dataset.language = lang;
    }
    function pathLanguage(path) {
      return new URL(path, API_ORIGIN).searchParams.get('lang') || lang;
    }

    function message(text, error = false) {
      status.textContent = text;
      status.hidden = !text;
      if (error) status.dataset.error = 'true'; else delete status.dataset.error;
    }
    function readState() {
      try {
        const probe = STORAGE_KEY + '-probe';
        sessionStorage.setItem(probe, '1');
        sessionStorage.removeItem(probe);
        const saved = sessionStorage.getItem(STORAGE_KEY);
        return saved && saved.length < 16384 ? saved : null;
      } catch { throw payloadError(0, 'storage_unavailable'); }
    }
    function saveState(value) {
      if (value !== null && (typeof value !== 'string' || value.length > 16384)) throw payloadError(0, 'invalid_state');
      try { if (value) sessionStorage.setItem(STORAGE_KEY, value); else sessionStorage.removeItem(STORAGE_KEY); }
      catch { throw payloadError(0, 'storage_unavailable'); }
      state = value;
    }
    function consumeDraft(form) {
      const occurrenceInput = form.elements.namedItem('occurrence_id');
      if (!occurrenceInput) return null;
      const identity = eventIdentity(location.href, occurrenceInput.value);
      if (!identity) return null;
      let raw;
      try {
        raw = sessionStorage.getItem(DRAFT_KEY);
        if (raw) sessionStorage.removeItem(DRAFT_KEY);
      } catch { return null; }
      if (!raw || raw.length > 65536) return null;
      const draft = parseDraft(raw, identity);
      if (!draft || !restoreDraft(form, draft)) return null;
      return draft;
    }

    function accountAccess(main) {
      const form = main.querySelector('[data-oe-booking-form]');
      const email = form?.elements.namedItem('contact_email');
      const occurrence = form?.elements.namedItem('occurrence_id');
      const tickets = form?.querySelector('[data-oe-step="tickets"]');
      const freeSingle = form?.dataset.oePaid === 'false' &&
        !form.querySelector('[name="registration_option"][value]:not([value="single"])');
      const identity = eventIdentity(location.href, occurrence?.value);
      if (!form) return null;
      if (!email || !occurrence || !tickets || !identity || root.dataset.accountsEnabled !== 'true') {
        const unavailable = document.createElement('p');
        unavailable.className = 'to-event-account';
        unavailable.setAttribute('role', 'status');
        unavailable.tabIndex = -1;
        unavailable.textContent = lang === 'en' ?
          'Account sign-in is unavailable. Reload this page and try again, or contact info@technicaloutdoor.com.' :
          'Akses akun belum tersedia. Muat ulang halaman dan coba lagi, atau hubungi info@technicaloutdoor.com.';
        (tickets || form).append(unavailable);
        form.addEventListener('submit', (event) => {
          event.preventDefault();
          event.stopImmediatePropagation();
          unavailable.focus();
        });
        return { ready: () => false, label: () => lang === 'en' ? 'Account unavailable' : 'Akun belum tersedia',
          begin: () => { unavailable.focus(); return false; } };
      }
      const profile = { email: customerEmail, name: root.dataset.customerName, phone: root.dataset.customerPhone };
      prefillAccountFields(form, profile);
      const panel = document.createElement('div');
      panel.className = 'to-event-account';
      const note = document.createElement('p');
      note.setAttribute('role', 'status');
      note.setAttribute('aria-live', 'polite');
      const useEmail = document.createElement('button');
      useEmail.type = 'button';
      useEmail.className = 'to-event-account__use-email';
      useEmail.textContent = lang === 'en' ? 'Use my account email' : 'Gunakan email akun saya';
      panel.append(note, useEmail);
      tickets.append(panel);
      const signedInNote = document.createElement('p');
      signedInNote.className = 'to-event-account';
      signedInNote.dataset.accountSignedIn = '';
      signedInNote.setAttribute('role', 'status');
      signedInNote.hidden = true;
      form.querySelector('[data-oe-step="contact"] h3')?.after(signedInNote);
      const emailLabel = email.labels?.[0];
      const originalEmailLabel = emailLabel?.textContent;
      function setNote(value) {
        if (note.textContent !== value) note.textContent = value;
      }
      function accountState() { return registrationAccountState(customerEmail, email.value); }
      function update() {
        const status = accountState();
        useEmail.hidden = status !== 'mismatch';
        signedInNote.hidden = freeSingle || status !== 'signed_in';
        note.hidden = freeSingle && status === 'signed_in';
        panel.hidden = freeSingle && status === 'signed_in';
        if (emailLabel) emailLabel.textContent = status === 'signed_in' ?
          (lang === 'en' ? 'Account email *' : 'Email akun *') : originalEmailLabel;
        if (status === 'signed_in') {
          const confirmation = freeSingle ?
            (lang === 'en' ? `Signed in as ${customerEmail}.` : `Masuk sebagai ${customerEmail}.`) :
            (lang === 'en' ?
              `You’re signed in with ${customerEmail}. Continue with your race details using this Technical Outdoor account.` :
              `Kamu sudah masuk dengan ${customerEmail}. Lanjutkan pendaftaran lomba memakai akun Technical Outdoor ini.`);
          setNote(confirmation);
          signedInNote.textContent = confirmation;
        }
        if (status === 'mismatch') {
          setNote(lang === 'en' ?
            'Your saved contact email differs from the account you signed in to. Use your account email to keep this booking with your purchases. Your other entries are still saved.' :
            'Email kontak yang tersimpan berbeda dari akun yang sedang masuk. Gunakan email akunmu agar pendaftaran dan pembelian ada dalam satu akun. Data lainnya tetap tersimpan.');
        } else if (status === 'sign_in') {
          setNote(freeSingle ? (lang === 'en' ?
            'Verify your email with the code we send, then enter your participant details. If you are new, this also creates your Technical Outdoor account.' :
            'Verifikasi email dengan kode yang kami kirim, lalu isi data peserta. Jika belum punya akun Technical Outdoor, akunmu akan dibuat otomatis.') :
            (lang === 'en' ?
              'Already have a Technical Outdoor account? Use the same email to sign in to your existing account. New here? The email-code step creates your account. Then enter your race details once; your ticket choices will be kept.' :
              'Sudah punya akun Technical Outdoor? Pakai email yang sama untuk masuk ke akunmu. Belum punya akun? Akun akan dibuat saat kamu memasukkan kode dari email. Setelah itu, isi data lomba sekali saja; pilihan tiketmu tetap tersimpan.'));
        }
      }
      useEmail.addEventListener('click', () => {
        email.value = customerEmail;
        prefillAccountFields(form, profile);
        update();
        email.dispatchEvent(new Event('change', { bubbles: true }));
      });
      email.addEventListener('input', update);
      email.addEventListener('change', update);
      update();
      return {
        ready: () => accountState() === 'signed_in',
        label: () => accountState() === 'mismatch' ?
          (lang === 'en' ? 'Check account email' : 'Periksa email akun') :
          (lang === 'en' ? 'Create account / sign in' : 'Buat akun / masuk'),
        begin() {
          if (accountState() === 'signed_in') return true;
          update();
          if (accountState() === 'mismatch') { useEmail.focus(); return false; }
          const sameEvent = initialIdentity?.event === identity.event;
          const login = storefrontLoginUrl(root.dataset.storefrontLoginUrl, location.href,
            sameEvent && initialPackageHint, sameEvent ? initialPreviewThemeId : '', occurrence.value,
            email.checkValidity() ? email.value : '');
          if (!login) {
            setNote(lang === 'en' ? 'Account sign-in is unavailable. Reload this page and try again, or contact info@technicaloutdoor.com.' :
              'Akses akun belum tersedia. Muat ulang halaman dan coba lagi, atau hubungi info@technicaloutdoor.com.');
            return false;
          }
          const draftIdentity = eventIdentity(location.href, occurrence.value);
          if (!draftIdentity) return false;
          const draft = {
            version: 1, ...draftIdentity, created: Date.now(),
            stage: main.dataset.bookingStage === 'tickets' ? 'contact' : main.dataset.bookingStage || 'contact',
            fields: draftFields(form),
          };
          try { storeDraft(sessionStorage, draft); }
          catch {
            setNote(lang === 'en' ? 'We could not save your entries. Keep this page open and try again.' :
              'Data yang kamu isi belum berhasil disimpan. Tetap di halaman ini dan coba lagi.');
            return false;
          }
          const stage = main.dataset.bookingStage;
          const key = currentEventKey(form);
          const startTracked = BOOKING_STAGES.has(stage) &&
            track('toi_booking_account_opened', bookingPayload({ stage }, key));
          if (startTracked) {
            // A return is measured only if this departure was observed with consent.
            draft.analyticsStart = true;
            try { storeDraft(sessionStorage, draft); } catch { /* Analytics cannot block sign-in. */ }
          }
          const settle = window.TOIAnalytics?.settleLast;
          if (startTracked && typeof settle === 'function') {
            // An independent deadline keeps navigation safe even if a publisher
            // replacement returns a promise that never settles.
            void Promise.race([
              Promise.resolve().then(() => settle()).catch(() => {}),
              new Promise(resolve => setTimeout(resolve, 150)),
            ]).then(() => location.assign(login));
          } else location.assign(login);
          return false;
        },
      };
    }
    function clearBlobs() { blobs.forEach((url) => URL.revokeObjectURL(url)); blobs = []; }
    function makeBlobUrl(blob) { const url = URL.createObjectURL(blob); blobs.push(url); return url; }

    function safeElements(main) {
      main.querySelectorAll(REMOVE).forEach((element) => element.remove());
      for (const node of [main, ...main.querySelectorAll('*')]) {
        for (const attr of [...node.attributes]) {
          const key = attr.name.toLowerCase();
          if (key.startsWith('on') || ['srcdoc', 'formaction', 'http-equiv', 'srcset', 'ping'].includes(key) ||
              (key === 'style' && /url\s*\(|expression\s*\(|@import|behavior\s*:/i.test(attr.value))) node.removeAttribute(attr.name);
        }
        if (node.hasAttribute('src') && node.tagName !== 'IMG') node.removeAttribute('src');
      }
      return main;
    }

    function wireLinks(main) {
      for (const link of main.querySelectorAll('a[href]')) {
        const raw = link.getAttribute('href');
        if (raw.startsWith('#')) continue;
        const internal = internalPath(raw);
        if (internal) {
          const mapped = siteUrl(internal, pathLanguage(internal));
          link.href = mapped;
          link.dataset.internalTarget = internal;
          if (isBinaryPath(internal)) link.dataset.binaryTarget = internal;
          link.removeAttribute('target');
          continue;
        }
        const checkout = checkoutUrl(raw, lang);
        if (checkout) {
          link.href = checkout;
          link.dataset.checkoutTarget = checkout;
          link.target = '_self';
          link.rel = 'noreferrer noopener';
          continue;
        }
        let external;
        try { external = new URL(raw, location.origin); } catch { external = null; }
        if (external && external.protocol === 'https:' &&
            ['www.technicaloutdoor.com', 'technicaloutdoor.com'].includes(external.hostname)) {
          if (['/pages/events', '/en/pages/events'].includes(external.pathname)) {
            external.pathname = (lang === 'en' ? '/en' : '') + '/pages/events';
          }
          link.href = external.href;
          link.rel = 'noreferrer noopener';
          continue;
        }
        if (external && external.protocol === 'https:' && !external.username && !external.password &&
            !external.port && external.hostname !== 'kachang-hub.onrender.com' &&
            !external.hostname.endsWith('.onrender.com') &&
            !/(^|\/)(?:checkouts|invoices)(?:\/|$)/i.test(external.pathname) &&
            !/(?:shopify|doku)/i.test(external.hostname)) {
          link.href = external.href;
          link.target = '_blank';
          link.rel = 'noreferrer noopener';
          continue;
        }
        if (raw === 'mailto:info@technicaloutdoor.com') continue;
        link.removeAttribute('href');
        link.setAttribute('aria-disabled', 'true');
      }
      for (const button of main.querySelectorAll('[data-copy-url]')) {
        const raw = button.dataset.copyUrl;
        const internal = internalPath(raw);
        const mapped = internal ? siteUrl(internal, lang) : null;
        if (mapped) button.dataset.copyUrl = mapped;
        else button.removeAttribute('data-copy-url');
      }
    }

    async function wireImages(main, version) {
      for (const image of main.querySelectorAll('img[src]')) {
        const path = internalPath(image.getAttribute('src'));
        image.removeAttribute('src');
        if (!path || !isBinaryPath(path)) continue;
        try {
          const result = await bridge(api, path, 'GET', {}, state);
          if (version !== renderVersion) return;
          if (result.binary && result.type.toLowerCase().startsWith('image/')) image.src = makeBlobUrl(result.binary);
        } catch { /* Alternative poster text remains available. */ }
      }
    }

    function enhance(main, resumed = null) {
      const quantity = main.querySelector('[data-oe-quantity]');
      if (quantity) {
        const update = () => updateRegistrationControls(main, quantity, lang);
        quantity.addEventListener('change', update);
        main.querySelectorAll('[name="registration_option"]').forEach((option) => option.addEventListener('change', update));
        update();
      }
      const account = accountAccess(main);
      window.TechnicalOutdoorBooking?.enhance(main, lang, {
        resumeStage: resumed?.stage, account,
        eventKey: currentEventKey(main.querySelector('[data-oe-booking-form]')),
      });
      main.querySelectorAll('[data-copy-url]').forEach((button) => button.addEventListener('click', async () => {
        try { await navigator.clipboard.writeText(button.dataset.copyUrl); button.textContent = labels.copied; }
        catch { message(labels.copyFailed, true); }
      }));
      return account;
    }

    async function showBinary(result, path) {
      const name = filenameFrom(result.disposition, /poster/.test(path) ? 'technical-outdoor-poster.png' : 'technical-outdoor-download');
      const link = document.createElement('a');
      link.href = makeBlobUrl(result.binary);
      link.download = name;
      document.body.append(link); link.click(); link.remove();
      message(labels.ready);
    }

    async function render(data, requested, navigation = 'none', packageUrl = null, allowResume = false) {
      const parsed = new DOMParser().parseFromString(data.html, 'text/html');
      const source = parsed.querySelector('main.oe');
      if (!source) throw payloadError(502, 'invalid_html');
      setLanguage(parsed.documentElement.lang);
      const main = safeElements(document.importNode(source, true));
      // The storefront already has its own Shopify language selector. The Hub
      // switch changes only the embedded form and would leave the header stale.
      main.querySelector('.oe-brand .oe-actions[aria-label="Language / Bahasa"]')?.remove();
      let destination = internalPath(data.location || '');
      if (!destination) destination = internalPath(main.querySelector('[data-oe-management-url]')?.getAttribute('href') || '');
      if (!destination) destination = requested;
      const mapped = siteUrl(destination, lang);
      if (mapped && mapped !== location.href && navigation !== 'none') {
        history[navigation === 'push' ? 'pushState' : 'replaceState']({}, '', mapped);
      }
      clearBlobs();
      content.replaceChildren(main);
      const analyticsMarker = main.querySelector('[data-oe-analytics-revoke-token]');
      if (analyticsMarker) {
        try { window.TOIAnalytics?.rememberRevocation?.(analyticsMarker.dataset.oeAnalyticsRevokeToken,
          Number(analyticsMarker.dataset.oeAnalyticsRevokeExpiresMs)); } catch { /* Optional analytics only. */ }
      }
      wireLinks(main);
      const packageSelected = packageUrl && requested === pagePath(packageUrl) && selectRequestedPackage(main, packageUrl);
      const bookingForm = main.querySelector('[data-oe-booking-form]');
      const resumed = allowResume && bookingForm ? consumeDraft(bookingForm) : null;
      const contactEmail = bookingForm?.elements.namedItem('contact_email');
      if (customerEmail && contactEmail && !contactEmail.value.trim()) contactEmail.value = customerEmail;
      const account = enhance(main, resumed);
      if (resumed?.analyticsStart === true && bookingForm &&
          registrationAccountState(customerEmail, contactEmail?.value) === 'signed_in') {
        const key = currentEventKey(bookingForm);
        if (key) track('toi_booking_account_returned', { event_key: key });
      }
      if (packageSelected && !resumed) main.querySelector('[data-oe-event-hero] a[href="#oe-register"]')?.click();
      message(resumed ? (lang === 'en' ? 'Your entries were restored. Review the event details and confirm the required agreements before submitting.' : 'Data yang tadi kamu isi sudah kembali. Cek info acara dan baca kembali persyaratannya sebelum mencentang persetujuan dan mengirim pendaftaran.') : '');
      if (startPending && requested === pagePath(initialEventUrl)) {
        startPending = false;
        const selected = bookingForm?.elements.namedItem('occurrence_id')?.value;
        const initialOccurrence = new URL(initialEventUrl).searchParams.get('occurrence');
        const action = bookingForm && internalPath(bookingForm.getAttribute('action'));
        const exactFreeForm = initialIdentity && selected && (!initialOccurrence || initialOccurrence === selected) &&
          eventIdentity(initialEventUrl, selected)?.event === initialIdentity.event &&
          action && new URL(action, API_ORIGIN).pathname === `/events/${initialIdentity.event}/register` &&
          main.classList.contains('to-booking-free') && bookingForm.dataset.oeHasErrors !== 'true' &&
          !main.querySelector('.oe-error,.oe-notice--error,[aria-invalid="true"]');
        if (exactFreeForm && account) {
          // The explicit calendar-click intent is single use. Canceled login,
          // Back and reload must never open Shopify again on their own.
          if (!account.ready()) account.begin();
        }
      }
      const version = ++renderVersion;
      void wireImages(main, version);
      syncBookingAnalytics();
      return main;
    }

    function startCheckout(link) {
      const destination = checkoutUrl(link.dataset.checkoutTarget, lang);
      const booking = bookingContext(location.href);
      if (!destination || !booking) { message(labels.failed, true); return false; }
      try {
        sessionStorage.setItem(RETURN_KEY, JSON.stringify({ ...booking, created: Date.now() }));
      } catch { message(labels.storage, true); return false; }
      track('toi_booking_checkout_opened', {});
      location.assign(destination);
      return true;
    }

    async function load(path, navigation = 'none', freshRetry = false, packageUrl = null) {
      if (busy) return;
      setLanguage(pathLanguage(path));
      busy = true;
      failedLoad = null;
      retry.hidden = true;
      message(labels.load);
      try {
        const result = await bridge(api, path, 'GET', {}, state);
        if (result.binary) await showBinary(result, path);
        else {
          if ('state' in result) saveState(result.state);
          await render(result, path, navigation, packageUrl, true);
        }
      } catch (error) {
        if (error.code === 'session_expired' && !freshRetry) {
          saveState(null);
          message(labels.expired, true);
          const recoveryPath = '/events/registration/recover?lang=' + lang;
          const recovery = siteUrl(recoveryPath, lang);
          if (path !== recoveryPath) history.replaceState({}, '', recovery);
          busy = false;
          await load(recoveryPath, 'none', true);
          if (content.querySelector('main.oe')) message(labels.expired, true);
          return;
        }
        message(error.code === 'storage_unavailable' ? labels.storage :
          error.code === 'session_expired' ? labels.expired : labels.failed, true);
        // Only retry an initial read that left the page empty. Never replay a
        // booking submission or replace a form containing the guest's entries.
        if (!content.querySelector('main.oe') &&
            !['storage_unavailable', 'session_expired'].includes(error.code)) {
          failedLoad = { path, navigation, freshRetry, packageUrl };
          retry.hidden = false;
        }
      } finally { busy = false; }
    }

    root.addEventListener('click', async (event) => {
      const checkout = event.target.closest('a[data-checkout-target]');
      if (checkout && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
        event.preventDefault();
        if (busy) return;
        startCheckout(checkout);
        return;
      }
      const link = event.target.closest('a[data-internal-target]');
      if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      if (busy) return;
      const path = link.dataset.internalTarget;
      if (link.dataset.binaryTarget) {
        busy = true;
        try { const result = await bridge(api, path, 'GET', {}, state); if (result.binary) await showBinary(result, path); else message(labels.failed, true); }
        catch { message(labels.failed, true); }
        finally { busy = false; }
      } else {
        setLanguage(pathLanguage(path));
        const mapped = siteUrl(path, lang);
        history.pushState({}, '', mapped);
        await load(path, 'replace');
      }
    });

    root.addEventListener('submit', async (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.closest('main.oe')) return;
      event.preventDefault();
      if (busy) return;
      const target = internalPath(form.getAttribute('action'));
      if (!target) { message(labels.failed, true); return; }
      const entries = [...new FormData(form, event.submitter || undefined).entries()];
      if (entries.some(([, value]) => typeof value !== 'string')) { message(labels.failed, true); return; }
      const fields = Object.fromEntries(entries);
      const method = (form.getAttribute('method') || 'get').toUpperCase();
      if (method === 'GET') {
        const next = new URL(target, API_ORIGIN);
        for (const [key, value] of entries) next.searchParams.set(key, value);
        const path = internalPath(next.href);
        if (!path) { message(labels.failed, true); return; }
        setLanguage(pathLanguage(path));
        history.pushState({}, '', siteUrl(path, lang));
        await load(path, 'replace');
        return;
      }
      if (method !== 'POST') { message(labels.failed, true); return; }
      const finalBooking = form.matches('[data-oe-booking-form]') && form.closest('main.oe')?.dataset.bookingStage === 'review';
      const eventKey = finalBooking ? currentEventKey(form) : null;
      const sourceIdentity = finalBooking ? eventIdentity(location.href, fields.occurrence_id) : null;
      const freeCarryPost = eventKey === CARRY_KEY && form.dataset.oePaid === 'false' &&
        sourceIdentity?.event === CARRY_EVENT && sourceIdentity.occurrence === CARRY_OCCURRENCE &&
        new URL(target, API_ORIGIN).pathname === `/events/${CARRY_EVENT}/register`;
      let resultTracked = false;
      busy = true;
      const submit = event.submitter || form.querySelector('[type="submit"]');
      if (submit) submit.disabled = true;
      message(labels.load);
      if (finalBooking) track('toi_booking_submit', bookingPayload({ stage: 'review' }, eventKey));
      try {
        const result = await bridge(api, target, 'POST', fields, state);
        if (result.binary) await showBinary(result, target);
        else {
          if ('state' in result) saveState(result.state);
          const main = await render(result, target, 'push');
          const booking = bookingContext(location.href);
          if (finalBooking) {
            if (result.status >= 200 && result.status < 300 && booking &&
                !main.querySelector('[data-oe-booking-form], .oe-notice--error, .oe-error, [aria-invalid="true"]')) {
              // Hub accepted the registration; this says nothing about payment.
              track('toi_booking_submit_result', bookingPayload({ outcome: 'accepted' }, eventKey));
              const marker = main.querySelector('[data-oe-free-booking-confirmed]');
              if (freeCarryPost && marker?.dataset.event === CARRY_EVENT &&
                  marker.dataset.occurrence === CARRY_OCCURRENCE) {
                track('toi_free_booking_confirmed', { event_key: CARRY_KEY });
              }
              resultTracked = true;
            } else if (main.querySelector('[data-oe-booking-form][data-oe-has-errors="true"]') ||
                main.querySelector('[data-oe-booking-form] [aria-invalid="true"], [data-oe-booking-form] .oe-notice--error, [data-oe-booking-form] .oe-error')) {
              track('toi_booking_submit_result', bookingPayload({ outcome: 'validation' }, eventKey));
              resultTracked = true;
            }
          }
          const checkout = main.querySelector('a[data-oe-auto-checkout][data-checkout-target]');
          if (result.status >= 200 && result.status < 300 && booking &&
              autoCheckoutPost(target, fields, booking) && checkout) startCheckout(checkout);
        }
      } catch (error) {
        if (finalBooking && !resultTracked) track('toi_booking_submit_result', bookingPayload({ outcome: error.code === 'session_expired' ? 'session_expired' : error?.name === 'TypeError' ? 'network' : 'unknown' }, eventKey));
        if (error.code === 'session_expired') {
          saveState(null);
          message(labels.expired, true);
          const recovery = siteUrl('/events/registration/recover?lang=' + lang, lang);
          history.replaceState({}, '', recovery);
          busy = false;
          await load('/events/registration/recover?lang=' + lang, 'none');
          if (content.querySelector('main.oe')) message(labels.expired, true);
          return;
        }
        message(error instanceof TypeError ? labels.network : labels.failed, true);
      } finally { busy = false; if (submit?.isConnected) submit.disabled = false; }
    });

    window.addEventListener('popstate', () => {
      const path = pagePath(location.href);
      if (path) void load(path, 'none'); else message(labels.invalid, true);
    });
    window.addEventListener('pageshow', (event) => {
      // A back navigation from Shopify can restore an old Liquid snapshot.
      // Reload both Shopify identity and the Hub's current event/form authority.
      if (event.persisted) location.reload();
    });
    try { state = readState(); }
    catch { message(labels.storage, true); return; }
    const initial = pagePath(location.href);
    if (!initial) { message(labels.invalid, true); return; }
    void load(initial, 'replace', false, location.href);
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = {
    internalPath, pagePath, siteUrl, isBinaryPath, checkoutUrl, filenameFrom, language, updateRegistrationControls,
    bookingContext, checkoutReturn, selectRequestedPackage, eventIdentity, loginReturnPath,
    storefrontLoginUrl, draftFields, parseDraft, restoreDraft, storeDraft, accountCheckIsCurrent, registrationAccountState, prefillAccountFields, bridge,
    eventKeyForForm,
  };
  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', install, { once: true });
    else install();
  }
})();
